<template>
  <div>
    <template v-if="isUpdate">
      <edit-button v-if="isSubmit" @click="init()"></edit-button>
      <view-button v-else @click="init()"></view-button>
    </template>
    <b-modal
      v-model="dialog"
      size="lg"
      :title="title"
      hide-footer
      scrollable
      centered
      :busy="isLoading"
    >
      <form-wrapper @submit="submit" v-if="!isLoading">
        <b-row class="pb-0">
          <b-col cols="12" md="4">
            <input-field
              :disabled="isDisabled"
              v-model.trim="form.first_name"
              label="First Name"
              rules="required|max-50"
            ></input-field>
          </b-col>
          <b-col cols="12" md="4">
            <input-field
              v-model.trim="form.last_name"
              :disabled="isDisabled"
              label="Last Name"
              rules="required|max-50"
            ></input-field>
          </b-col>
          <b-col cols="12" md="4">
            <select-field
              v-model.trim="form.gender"
              :disabled="isDisabled"
              label="Gender"
              outlined
              :items="gender"
              rules="required|max-50"
            ></select-field>
          </b-col>
          <b-col cols="12" md="4">
            <date-picker
              :disabled="isDisabled"
              v-model="form.DOB"
              :rules="dobRules"
              :max="new Date()"
              label="Date Of Birth"
            ></date-picker>
          </b-col>
          <b-col cols="12" md="4" v-if="false">
            <select-field
              label="Blood Group"
              :items="bloodGroups"
              :disabled="isDisabled"
              v-model="form.blood_id"
              rules="required"
            ></select-field>
          </b-col>
          <!-- Phase 1 Corrections - Label changed from "Contact Type" to "Role" -->
          <b-col cols="12" md="4">
            <select-field
              v-model.trim="form.contact_id"
              label="Role"
              :disabled="isDisabled"
              :items="contactTypes"
              rules="required"
            ></select-field>
          </b-col>
          <b-col cols="12" md="4">
            <input-field
              v-model.trim="form.email_id"
              label="Email"
              :disabled="isEmailDisabled"
              rules="required|email|max-100"
            ></input-field>
          </b-col>
          <b-col cols="12" md="4">
            <phone-field
              :disabled="isDisabled"
              v-model.trim="form.phone_number_1"
              :defaultCountry="form.country_code_1"
              label="Phone Number 1"
              @onCountryCode="form.country_code_1 = $event"
              rules="required|integer"
            ></phone-field>
          </b-col>
          <b-col cols="12" md="4">
            <phone-field
              v-model.trim="form.phone_number_2"
              :disabled="isDisabled"
              :defaultCountry="form.country_code_2"
              label="Phone Number 2"
              @onCountryCode="form.country_code_2 = $event"
              rules="integer"
            ></phone-field>
          </b-col>
          <!-- Phase 1 Corrections - Label changed from "Organs" to "Organ and Tissue" -->
          <b-col cols="12" md="4" v-if="isUserPage && isOrgan">
            <autocomplete
              :disabled="isDisabled"
              v-model.trim="form.contact_organ_mapping"
              :items="organs"
              rules="required"
              label="Organ and Tissue"
              multiple
            ></autocomplete>
          </b-col>
          <b-col cols="12" md="4">
            <autocomplete
              v-model.trim="form.qualification_details"
              :disabled="isDisabled"
              multiple
              :items="masterQualifications"
              label="Qualification Details"
            ></autocomplete>
          </b-col>
          <!-- Phase 1 - Other Qualification Text Field -->
          <b-col cols="12" md="4" v-if="hasOtherQualification">
            <input-field
              v-model.trim="form.other_qualification_name"
              :disabled="isDisabled"
              label="Specify Other Qualification"
              :rules="hasOtherQualification ? 'required|max-100' : ''"
            >
              <template v-slot:help>
                <help text="Please specify the qualification details"></help>
              </template>
            </input-field>
          </b-col>
          <!-- Phase 1 Corrections - Point 4b: KMC Registration Number for Transplant Team Members -->
          <b-col cols="12" md="4" v-if="isSurgeonRole">
            <input-field
              v-model.trim="form.kmc_registration_number"
              :disabled="isDisabled"
              label="KMC Registration Number"
              rules=""
            >
              <template v-slot:help>
                <help text="Karnataka Medical Council Registration Number"></help>
              </template>
            </input-field>
          </b-col>
          <!-- Phase 1 Corrections - Point 4b: HPR Number for Transplant Team Members -->
          <b-col cols="12" md="4" v-if="isSurgeonRole">
            <input-field
              v-model.trim="form.hpr_number"
              :disabled="isDisabled"
              label="HPR Number"
              rules=""
            >
              <template v-slot:help>
                <help text="Healthcare Professionals Registry Number - verified through ABDM integration"></help>
              </template>
            </input-field>
          </b-col>
          <!-- Phase 1 - KMC Document Upload -->
          <b-col cols="12" md="4" v-if="isSurgeonRole">
            <file-input-group
              :disabled="isDisabled"
              v-model="form.kmc_document"
              accept=".jpg,.jpeg,.png,.pdf"
              rules="file-image-pdf"
              label="KMC Document"
            >
              <template v-slot:help>
                <help text="Upload KMC Registration Certificate (PDF, JPG, PNG)"></help>
              </template>
            </file-input-group>
          </b-col>
          <b-col
            cols="12"
            md="4"
            :class="isFile ? '' : 'd-flex align-items-center'"
          >
            <file-input-group
              :disabled="isDisabled"
              v-model="form.photo"
              accept=".jpg,capture=camera,.jpeg,.png,.pdf"
              rules="file-image"
              label="Photo"
            ></file-input-group>

            <!-- <file-input
              v-if="isFile"
              v-model="form.photo"
              :disabled="isDisabled"
              accept="image/*"
              label="Photo"
              rules="file-image"
            >
              <template v-slot:help>
                <help text="Optional Field"></help>
              </template>
            </file-input>
            <b-button
              outlined
              style="height: 35px"
              class="mt-3"
              v-else
              @click="open(form.photo)"
              color="primary"
              small
            >
              View Photo
            </b-button> -->
          </b-col>
          <b-col cols="12" md="4" v-if="isUserPage && isAdmin">
            <yes-no
              v-model="form.ALF_approver"
              label="Is Supra Urgent Approver?"
            ></yes-no>
          </b-col>
          <b-col cols="12" md="4" v-if="isUserPage && isAdmin">
            <yes-no
              v-model="form.is_approved_doctor"
              label="Is Approved Treating Doctor?"
            ></yes-no>
          </b-col>
        </b-row>
        <div class="d-flex">
          <div class="flex-grow-1"></div>
          <b-button variant="outline-danger" @click="dialog = false"
            >Cancel
          </b-button>
          <b-button v-if="isEdit" class="ms-3" variant="success" type="submit"
            >Submit
          </b-button>
        </div>
      </form-wrapper>
    </b-modal>
  </div>
</template>

<script>
import { GENDERS } from "../../assets/constants/hospital.contants";
import { getAge, getDateOnly, isArray, openFile } from "../../assets/js/common";
import userHelper from "./user.helper";
import EditButton from "../buttons/EditButton.vue";
import DatePicker from "../form/DatePicker.vue";
import PhoneField from "../form/PhoneField.vue";
import SelectField from "../form/SelectField.vue";
import userMixins from "../../assets/mixins/user.mixins";
import licenseService from "../../assets/services/license.service";
import ViewButton from "../buttons/ViewButton.vue";
import menuConstants from "../../assets/constants/menu.constants";
import FileInputGroup from "../form/FileInputGroup.vue";
import YesNo from "../form/YesNo.vue";
import Help from "../Help.vue";
export default {
  props: {
    item: {
      type: Object,
      default: () => {
        return {};
      },
    },
    hospital_id: {
      type: [Number, String],
      default: "",
    },
    index: {
      type: Number,
      default: null,
    },
    // Phase 1 Corrections - Default organ ID for pre-selection when adding contact from organ license page
    default_organ_id: {
      type: [Number, String],
      default: "",
    },
    // Phase 1 Corrections - Organ license ID for contact creation
    organ_license_id: {
      type: [Number, String],
      default: "",
    },
  },
  mixins: [userMixins],
  components: {
    EditButton,
    SelectField,
    PhoneField,
    DatePicker,
    ViewButton,
    FileInputGroup,
    YesNo,
    Help,
  },
  data() {
    return {
      dialog: false,
      isValid: false,
      isLoading: false,
      form: {
        DOB: null,
      },
      loading: true,
      updateDoc: {},
      organs: [],
    };
  },
  watch: {
    dialog(val) {
      if (val == true) {
        this.init();
      }
    },
  },
  computed: {
    dobRules() {
      let dob = this.form.DOB;
      if (dob) {
        if (getAge(dob) < 18) return "age-18";
      }
      return "";
    },
    isSubmit() {
      if (this.isUpdate) {
        return this.item.status == 1 && this.isAdmin;
      }
      return this.isHospitalAdmin || this.isAdmin;
    },
    isFile() {
      let { photo } = this.form;
      return photo == null || photo instanceof File;
    },
    gender() {
      return GENDERS;
    },
    title() {
      return this.item.first_name ? "Update User" : "Add User";
    },
    contactTypes() {
      return this.masterContactTypes.filter((item) => {
        if (this.isTranstanPage) return item.contact_type == 5;
        return item.contact_type == 1 || item.contact_type == 2;
      });
    },
    isUser() {
      return this.$route.name == "user-management";
    },

    master() {
      return this.$store.getters.master;
    },
    isUpdate() {
      return this.item.hospital_contact_id;
    },
    isEdit() {
      let { USER, TRANSTAN_USER_LIST } = menuConstants;
      let id = this.isUser ? USER : TRANSTAN_USER_LIST;
      let hasCreateAccess = this.hasCreateAccess(id);
      if (!this.isUpdate) return hasCreateAccess;
      let hasAccess = this.hasEditAccess(id);
      return hasAccess && this.status != 4;
    },
    isDisabled() {
      return !this.isEdit;
    },
    isEmailDisabled() {
      return this.isUpdate != undefined;
    },
    isOrgan() {
      let user = this.contactTypes.find((item) => {
        return item.value == this.form.contact_id;
      });
      if (user == null) return false;
      return user.contact_type == 2;
    },
    // Phase 1 Corrections - Point 4b: Show KMC and HPR fields for Surgeon roles
    isSurgeonRole() {
      // contact_id 2 = Surgeon, 5 = Junior Surgeon (check master_contact table)
      let surgeonRoles = ['2', '5', 2, 5];
      return surgeonRoles.includes(this.form.contact_id);
    },
    // Phase 1 - Check if "Other" qualification is selected
    hasOtherQualification() {
      if (!this.form.qualification_details || !Array.isArray(this.form.qualification_details)) {
        return false;
      }
      // Find the "Other" qualification in the master list
      let otherQual = this.masterQualifications.find(
        (item) => item.name && item.name.toLowerCase() === 'other'
      );
      if (!otherQual) return false;
      // Check if "Other" is in the selected qualifications
      return this.form.qualification_details.includes(otherQual.value) ||
             this.form.qualification_details.includes(otherQual.value.toString());
    },
  },
  methods: {
    open(url) {
      openFile(url);
    },
    reset() {
      this.form = {
        first_name: "",
        last_name: "",
        gender: "",
        photo: null,
        email_id: "",
        contact_id: "",
        blood_id: "",
        country_code_1: "91",
        phone_number_1: "",
        country_code_2: "91",
        phone_number_2: "",
        individual_id: "",
        hospital_id: this.hospital_id,
        status: 1,
        DOB: "",
        hospital_contact_id: "",
        age: "",
        age_registered_date: getDateOnly(),
        created_by: this.created_by,
        qualification_details: [],
        registration_id: "",
        ALF_approver: "",
        is_approved_doctor: "",
        contact_organ_mapping: [],
        // Phase 1 Corrections - Point 4b: Transplant Team Member fields
        kmc_registration_number: "",
        hpr_number: "",
        kmc_document: null,
        // Phase 1 - Other qualification
        other_qualification_name: "",
      };
      // Phase 1 Corrections - Pre-select organ if default_organ_id is provided
      if (this.default_organ_id) {
        this.form.contact_organ_mapping = [this.default_organ_id.toString()];
      }
    },
    async init() {
      const vm = this;
      let { hospital_id } = vm;
      vm.reset();
      if (vm.isUpdate) {
        userHelper.get(this);
      }
      if (vm.isUser) {
        try {
          vm.list = [];
          let doc = { hospital_id, dml_indicator: "UL" };
          let { data } = await licenseService.list(doc);
          let { status, msg, list } = data;
          if (!status) {
            vm.$alert.show(msg);
            return;
          }
          //need to enable after full live sotto phase1 16/12/25 by rajkumar
          // list = list.filter((item) => {
          //   return item.status == 2;
          // });
          if (isArray(list)) {
            vm.organs = list.map((item) => {
              let { organ_license_id } = item;
              return {
                name: item.organ_name,
                value: item.organ_id.toString(),
                organ_license_id,
              };
            });
            console.log(list);
          }
        } catch (error) {
          vm.$alert.show(error.message);
        }
      }
    },
    async submit() {
      userHelper.createUser(this);
    },
  },
};
</script>

<style></style>
